import { environment } from './environmentService';
import { getFullUrl } from '../analytics';

type AuthenticationStatus =
  | 'taxReferralFormAuthenticatedUser'
  | 'taxReferralFormUnauthenticatedUser'
  | 'unknown';

// TRUSTED-96028: temporary check appended only to the url reported on the
// Page Viewed event (never the real browser URL/address bar) to measure what
// portion of visitors reaching these pages are already signed in to
// ramseysolutions.com. Same-origin HEAD request against the site masthead's
// own auth check (Magnolia embeds these pages into its own document, so this
// never crosses origins and never touches the profile data that endpoint's
// GET would return). `credentials: 'same-origin'` depends on that embedding
// staying same-origin — if it ever doesn't (a different preview/QA embed
// path, a future cross-subdomain change), no auth cookie is sent and every
// visitor is tagged unauthenticated, not unknown, silently understating the
// real authenticated rate rather than surfacing as a fetch failure.
async function getAuthenticationTaggedUrl(): Promise<string | undefined> {
  if (environment() !== 'prod') {
    return undefined;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 2000);
  let status: AuthenticationStatus;

  try {
    const response = await fetch('/forms/martech/auth/masthead/api/user', {
      method: 'HEAD',
      credentials: 'same-origin',
      redirect: 'manual',
      signal: controller.signal
    });

    if (response.status === 200) {
      status = 'taxReferralFormAuthenticatedUser';
    } else if (response.status === 403) {
      status = 'taxReferralFormUnauthenticatedUser';
    } else {
      status = 'unknown';
    }
  } catch {
    status = 'unknown';
  } finally {
    clearTimeout(timeoutId);
  }

  const url = getFullUrl();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}rt_auth_check=${status}`;
}

export { getAuthenticationTaggedUrl };
